'use client';

import { NEXT_ROUTES } from '@bts-web/utils-next-infrastructure';
import { AssetImage, ButtonBase, Drawer } from '../../../../common';
import { useClientTranslation } from '@bts-web/utils-lokalise';
import { css } from '@bts-web/utils-style-engine';
import { useRouter } from 'next/navigation';
import { TransfersSectionVariants } from '@bts-web/core-features/crypto-transfers';
import { useModalDrawerContext } from '../../../../common/components/ModalDrawerContext/ModalDrawerContext';
import { CryptoTransfersClientDrawer } from '../../../../trade/TradeDrawer/TradeDrawerProper/TradeOptionsDrawer';
import { useFeatureFlag } from '../../../../statsig/sdk/provider';

type UnownedAssetDrawerProps = {
  assetData: {
    id: string;
    name: string;
    logoUrl: string;
  };
};

const UnownedAssetDrawer = ({ assetData }: UnownedAssetDrawerProps) => {
  const { t } = useClientTranslation();

  const router = useRouter();

  const { openDrawer, closeAll } = useModalDrawerContext();

  const feature_hasCryptoTransfersEnabled = useFeatureFlag(
    'feature_hasCryptoTransfersEnabled',
  );

  const handleBuyClick = () => {
    closeAll();

    router.push(
      `${NEXT_ROUTES.ASSET_DETAILS.replace('[id]', assetData.id ?? '')}/buy`,
    );
  };

  const handleDepositClick = () => {
    closeAll();

    openDrawer(
      () => CryptoTransfersClientDrawer(TransfersSectionVariants.Deposit),
      {
        disableCloseButton: true,
        disableOverlayClose: true,
      },
    );
  };

  return (
    <Drawer>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: 'small',
          paddingX: 'large',
          paddingTop: 'small',
          paddingBottom: 'medium',
          alignItems: 'center',
          textAlign: 'center',
        })}
      >
        <h1>
          <AssetImage
            height={48}
            width={48}
            src={assetData.logoUrl ?? ''}
            alt={assetData.name ?? ''}
          />
        </h1>
        <div>
          {t('insufficient_staking_funds_title', {
            assetName: assetData.name,
          })}
        </div>
        <p
          className={css({
            color: 'neutrals.text_secondary',
            fontSize: 'body.medium',
            letterSpacing: 'body.medium',
            lineHeight: 'body.medium',
          })}
        >
          {t('insufficient_staking_funds_description', {
            assetName: assetData.name,
          })}
        </p>
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            gap: 'small',
            padding: 'small',
          })}
        >
          <ButtonBase visual="primary" onClick={handleBuyClick}>
            {t('insufficient_staking_buy_asset', { assetName: assetData.name })}
          </ButtonBase>
          {feature_hasCryptoTransfersEnabled && (
            <ButtonBase onClick={handleDepositClick}>
              {t('insufficient_staking_deposit_asset', {
                assetName: assetData.name,
              })}
            </ButtonBase>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export { UnownedAssetDrawer };
